import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Mail, Star, TrendingUp, Users, School, DollarSign, Lock } from 'lucide-react';
import colors from '../config/colors';

const AdaptedReportPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [matches, setMatches] = useState([]);
  const [childName, setChildName] = useState('your child');

  useEffect(() => {
    // Load matches from sessionStorage
    const storedMatches = sessionStorage.getItem('school_matches');
    const storedName = sessionStorage.getItem('child_name');
    
    if (!storedMatches) {
      // No matches available, redirect to questionnaire
      navigate('/questionnaire');
      return;
    }
    
    try {
      const parsedMatches = JSON.parse(storedMatches);
      // The backend returns { top_schools: [...], total_matches: N, ... }
      const schoolList = parsedMatches.top_schools || parsedMatches || [];
      setMatches(schoolList);
      setChildName(storedName || 'your child');
    } catch (error) {
      console.error('Error loading matches:', error);
      navigate('/questionnaire');
    }
  }, [navigate]);

  // Show loading state if no matches yet
  if (matches.length === 0) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: colors.bgPage,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', color: colors.textPrimary, marginBottom: '16px' }}>
            Loading your matches...
          </h2>
        </div>
      </div>
    );
  }

  const topMatch = matches[0] || {};
  
  const reportData = {
    childName: childName,
    topMatch: {
      school_name: topMatch.full_name || topMatch.common_name || 'School',
      location: (topMatch.campus_locations || [])[0] || 'KL',
      curriculum: topMatch.curriculum_framework || 'International',
      match_score: Math.round(topMatch.match_score || 0),
      key_features: topMatch.curriculum_framework || '',
      price_tier: "Included with Basic Report (RM 4.99)",
      description: `${(topMatch.campus_locations || [])[0] || ''} · ${topMatch.curriculum_framework || ''} · ${topMatch.common_name || ''}`,
      priority_alignment: ''
    },
    currentSchool: "Current School",
    feeProjection: [
      { year: "Year 7", current: "RM 42,000", nexus: "RM 38,500", savings: "+ RM 3,500" },
      { year: "Year 8", current: "RM 44,100", nexus: "RM 40,425", savings: "+ RM 3,675" },
      { year: "Year 9", current: "RM 46,305", nexus: "RM 42,446", savings: "+ RM 3,859" },
      { year: "Year 10 (IGCSE)", current: "RM 48,620", nexus: "RM 44,568", savings: "+ RM 4,052" },
      { year: "Year 11 (IGCSE)", current: "RM 51,051", nexus: "RM 46,797", savings: "+ RM 4,254" }
    ],
    totalSavings: "RM 19,340",
    parentReviews: [
      {
        name: "Sarah L.",
        rating: 5,
        text: "Nexus transformed my daughter's learning. The IB program is rigorous but supportive. Teachers genuinely care.",
        platform: "Google Reviews"
      },
      {
        name: "Ahmad R.",
        rating: 5,
        text: "Best decision we made. The international community is welcoming and the facilities are top-notch.",
        platform: "Facebook"
      },
      {
        name: "Michelle T.",
        rating: 4,
        text: "Strong academics and good pastoral care. Wish there were more after-school sports options.",
        platform: "SchoolAdvisor"
      }
    ],
    aiSummary: {
      pros: ["Outstanding IB implementation", "Caring, qualified teachers", "Strong sense of community"],
      cons: ["Limited sports facilities compared to larger schools", "Commute from KL can be challenging"]
    },
    nearMissSchools: [
      { name: "ISKL", reason: "Above budget (RM 75k+)", icon: "wallet" },
      { name: "Mont'Kiara International", reason: "Curriculum not IB", icon: "school" },
      { name: "Fairview Subang", reason: "Location too far (1hr+ commute)", icon: "location" }
    ]
  };

  const handleEmailPDF = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bgPage,
      padding: '40px 20px',
      paddingBottom: '120px'
    }}>
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        background: colors.textInverse,
        borderRadius: '20px',
        padding: 'clamp(32px, 5vw, 48px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        {/* Meta Badge */}
        <div style={{
          display: 'inline-block',
          background: colors.bgAccent,
          color: colors.textMuted,
          padding: '6px 16px',
          borderRadius: '100px',
          fontSize: '0.8rem',
          fontWeight: '600',
          marginBottom: '24px'
        }}>
          ✓ 121 schools screened · 5 shortlisted · 3 fee comparisons
        </div>

        {/* Match Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '600',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '8px'
          }}>
            YOUR MATCH
          </div>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            fontWeight: '700',
            background: `linear-gradient(135deg, ${colors.brandPrimary} 0%, #D84315 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            lineHeight: '1.2'
          }}>
            {reportData.topMatch.school_name}
          </h2>
          <div style={{
            fontSize: '0.95rem',
            color: colors.textMuted,
            marginBottom: '16px'
          }}>
            {reportData.topMatch.price_tier}
          </div>
          <div style={{
            display: 'inline-block',
            background: colors.brandPrimary,
            color: colors.textInverse,
            padding: '8px 20px',
            borderRadius: '100px',
            fontSize: '0.85rem',
            fontWeight: '700'
          }}>
            ✨ Best match · {reportData.topMatch.key_features}
          </div>
        </div>

        {/* Hero Match */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.brandPrimary}15 0%, ${colors.bgAccent} 100%)`,
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '40px',
          border: `2px solid ${colors.brandPrimary}30`
        }}>
          <div style={{
            fontSize: '0.95rem',
            fontWeight: '600',
            color: colors.brandPrimary,
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Priority alignment · {reportData.topMatch.priority_alignment}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: colors.brandPrimary,
              color: colors.textInverse,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.5rem',
              fontWeight: '700',
              flexShrink: 0
            }}>
              {reportData.topMatch.match_score}%
              <div style={{ fontSize: '0.7rem', fontWeight: '600', marginTop: '4px' }}>MATCH</div>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.textPrimary,
                marginBottom: '8px'
              }}>
                {reportData.topMatch.school_name}
              </h3>
              <div style={{
                fontSize: '1.05rem',
                color: colors.textSecondary,
                lineHeight: '1.6'
              }}>
                {reportData.topMatch.description}
              </div>
            </div>
          </div>
        </div>

        {/* Fee Projection */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: '700',
            color: colors.textPrimary,
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <TrendingUp size={24} color={colors.brandPrimary} />
            Fee projection vs Current school · Grade 7–11
          </h3>
          <p style={{
            fontSize: '0.9rem',
            color: colors.textMuted,
            marginBottom: '20px'
          }}>
            5-year comparison · {reportData.currentSchool} vs {reportData.topMatch.school_name}
          </p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.95rem'
            }}>
              <thead>
                <tr style={{ background: colors.bgAccent }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>Year</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700' }}>Current School</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700' }}>Nexus</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: colors.brandPrimary }}>Savings</th>
                </tr>
              </thead>
              <tbody>
                {reportData.feeProjection.map((row) => (
                  <tr key={row.year} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={{ padding: '12px', fontWeight: '600' }}>{row.year}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: colors.textSecondary }}>{row.current}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: colors.textSecondary }}>{row.nexus}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#2E7D32', fontWeight: '700' }}>{row.savings}</td>
                  </tr>
                ))}
                <tr style={{ background: colors.brandAccent, fontWeight: '700' }}>
                  <td style={{ padding: '16px' }}>Total (5 years)</td>
                  <td colSpan={2} style={{ padding: '16px', textAlign: 'right' }}>You save:</td>
                  <td style={{ padding: '16px', textAlign: 'right', color: '#2E7D32', fontSize: '1.2rem' }}>{reportData.totalSavings}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{
            fontSize: '0.85rem',
            color: colors.textMuted,
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            * Fees indexed at 5% annually. Excludes registration, deposits, uniforms, and optional fees.
          </div>
        </div>

        {/* Parent Voices */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '700',
            color: colors.brandPrimary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Users size={20} />
            PARENT VOICES · {reportData.topMatch.school_name.toUpperCase()}
          </div>

          {/* AI Summary */}
          <div style={{
            background: colors.bgAccent,
            borderLeft: `4px solid ${colors.brandPrimary}`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '600',
              color: colors.brandPrimary,
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ✨ AI SUMMARY · 47 reviews analyzed
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: '700', marginBottom: '8px', color: colors.textPrimary }}>What parents love:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: colors.textSecondary }}>
                {reportData.aiSummary.pros.map((pro) => (
                  <li key={pro} style={{ marginBottom: '4px' }}>{pro}</li>
                ))}
              </ul>
            </div>
            <div>
              <div style={{ fontWeight: '700', marginBottom: '8px', color: colors.textPrimary }}>Common concerns:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: colors.textSecondary }}>
                {reportData.aiSummary.cons.map((con) => (
                  <li key={con} style={{ marginBottom: '4px' }}>{con}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Review Cards */}
          <div style={{ display: 'grid', gap: '16px' }}>
            {reportData.parentReviews.map((review) => (
              <div key={review.name + review.platform} style={{
                background: colors.bgCard,
                border: `1px solid ${colors.borderMedium}`,
                borderRadius: '12px',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: '700', color: colors.textPrimary, marginBottom: '4px' }}>
                      {review.name}
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          fill={i < review.rating ? colors.brandPrimary : 'none'}
                          color={i < review.rating ? colors.brandPrimary : colors.borderMedium}
                        />
                      ))}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: colors.textMuted,
                    background: colors.bgAccent,
                    padding: '4px 12px',
                    borderRadius: '100px'
                  }}>
                    {review.platform}
                  </div>
                </div>
                <p style={{
                  fontSize: '0.95rem',
                  color: colors.textSecondary,
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  "{review.text}"
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade Teaser */}
        <div style={{
          background: 'linear-gradient(135deg, #2C2823 0%, #3D3834 100%)',
          borderRadius: '16px',
          padding: '32px',
          color: colors.textInverse,
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <Lock size={48} style={{ margin: '0 auto 16px', opacity: 0.7 }} />
          <h3 style={{
            fontSize: '1.6rem',
            fontWeight: '700',
            marginBottom: '12px'
          }}>
            See 4 More Perfectly Matched Schools
          </h3>
          <p style={{
            fontSize: '1.05rem',
            opacity: 0.9,
            marginBottom: '24px'
          }}>
            Detailed comparisons • Fee breakdowns • Full parent reviews
          </p>
          <button style={{
            background: '#FFD700',
            color: '#2C2823',
            padding: '14px 32px',
            borderRadius: '100px',
            fontWeight: '700',
            fontSize: '1.05rem',
            border: 'none',
            cursor: 'pointer'
          }}>
            Unlock Full Report (+RM10)
          </button>
        </div>

        {/* Near-Miss Schools */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '700',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '20px'
          }}>
            NEAR-MISS SCHOOLS · why they didn't make the cut
          </div>
          <div style={{ display: 'grid', gap: '12px' }}>
            {reportData.nearMissSchools.map((school) => (
              <div key={school.name} style={{
                background: colors.bgCard,
                borderLeft: `4px solid ${colors.textMuted}`,
                borderRadius: '8px',
                padding: '16px 20px'
              }}>
                <div style={{
                  fontWeight: '700',
                  color: colors.textPrimary,
                  marginBottom: '4px'
                }}>
                  {school.name}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: colors.textMuted
                }}>
                  {school.reason}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Metadata */}
        <div style={{
          fontSize: '0.8rem',
          color: colors.textMuted,
          paddingTop: '24px',
          borderTop: `1px solid ${colors.borderLight}`,
          lineHeight: '1.8'
        }}>
          <strong>Report generated:</strong> December 2025 • <strong>Filters:</strong> Year 10, Mont Kiara, RM30-50k budget, IB preference<br />
          <strong>Data sources:</strong> 47 parent reviews (Google, Facebook, SchoolAdvisor) · Internal tuition data · School websites<br />
          <strong>AI summary:</strong> Generated by GPT-5.2 from verified parent reviews
        </div>
      </div>

      {/* Fixed Email + PDF Section */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: colors.textInverse,
        borderTop: `2px solid ${colors.borderMedium}`,
        padding: '20px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              flex: 1,
              minWidth: '250px',
              padding: '14px 20px',
              borderRadius: '100px',
              border: `2px solid ${colors.borderMedium}`,
              fontSize: '1rem',
              outline: 'none'
            }}
          />
          <button
            onClick={handleEmailPDF}
            style={{
              background: colors.brandPrimary,
              color: colors.textInverse,
              padding: '14px 28px',
              borderRadius: '100px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem'
            }}
          >
            <Mail size={18} />
            Email PDF report
          </button>
          <button style={{
            background: 'transparent',
            color: colors.brandPrimary,
            padding: '14px 28px',
            borderRadius: '100px',
            fontWeight: '600',
            border: `2px solid ${colors.brandPrimary}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '1rem'
          }}>
            <Download size={18} />
            Download PDF
          </button>
        </div>
        <div style={{
          maxWidth: '900px',
          margin: '12px auto 0',
          fontSize: '0.8rem',
          color: colors.textMuted,
          textAlign: 'center'
        }}>
          We'll never spam you. Your email is safe with us.
        </div>
      </div>

      {/* Toast Message */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: colors.brandPrimary,
          color: colors.textInverse,
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          fontWeight: '600'
        }}>
          ✓ PDF report sent to {email}!
        </div>
      )}
    </div>
  );
};

export default AdaptedReportPage;
